/**
 * `/admin/hotline` - spec 23, the contacts on the member Fitzone Hotline screen.
 *
 * Every change is live on the member's next request (NFR-10.6). The photo is a
 * separate multipart request, exactly like a partner logo, so a storage outage
 * never loses the name and number the admin just typed.
 */

import { request } from "./client";

export type HotlineContact = {
  id: string;
  name: string;
  role: string | null;
  /** As the admin wrote it; the member app strips separators when dialling. */
  phone: string;
  email: string | null;
  photo_url: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
};

export type HotlineWrite = {
  name: string;
  phone: string;
  /** `null` clears it on an edit. */
  role: string | null;
  email: string | null;
};

export type HotlineOutcome = { contact: HotlineContact; message: string };

export function fetchHotlineContacts(): Promise<{ items: HotlineContact[] }> {
  return request<{ items: HotlineContact[] }>("/admin/hotline");
}

export function createHotlineContact(payload: HotlineWrite): Promise<HotlineOutcome> {
  return request<HotlineOutcome>("/admin/hotline", { method: "POST", body: payload });
}

export function updateHotlineContact(contactId: string, payload: Partial<HotlineWrite>): Promise<HotlineOutcome> {
  return request<HotlineOutcome>(`/admin/hotline/${contactId}`, { method: "PATCH", body: payload });
}

/** A real delete: the contact and its photo are gone. */
export function deleteHotlineContact(contactId: string): Promise<{ message: string }> {
  return request<{ message: string }>(`/admin/hotline/${contactId}`, { method: "DELETE" });
}

/** One audit row for the whole list. */
export function reorderHotlineContacts(
  items: Array<{ id: string; display_order: number }>,
): Promise<{ updated: number; message: string }> {
  return request("/admin/hotline/order", { method: "PUT", body: { items } });
}

/** Send the output of `optimizePhoto(file, { aspect: 1 })`, never the raw file. */
export function uploadHotlinePhoto(contactId: string, photo: Blob, fileName: string): Promise<HotlineOutcome> {
  const body = new FormData();
  body.append("photo", photo, fileName);
  return request<HotlineOutcome>(`/admin/hotline/${contactId}/photo`, { method: "PUT", body });
}

export function removeHotlinePhoto(contactId: string): Promise<HotlineOutcome> {
  return request<HotlineOutcome>(`/admin/hotline/${contactId}/photo`, { method: "DELETE" });
}
